import React, {
  ComponentProps,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { defineMessages } from 'react-intl';
import { useIntl } from 'react-intl';
import { Platform, StyleProp, StyleSheet, ViewStyle, View, Pressable } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import {
  Button,
  Carousel,
  CarouselDots,
  Container,
  ContentfulImageTransformOptions,
  ContentfulTextLinkify,
  DisplayText,
  FallbackImage,
  IconLink,
  pointerEventsStyles,
  theme,
  TYPE_CONFIG,
  useOpenURL,
  useResponsive,
  useToggleState,
} from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import { HomeBannerContent } from '../../hooks';

const HomeBannerFallbackImage = require('./home-banner-fallback.webp');

// ─────────────────────────────────────────────────────────────────────────────

export const HomeBanner = (props: HomeBannerProps) => {
  const { content, size, style, secondaryCtaLabel, onSecondaryCtaPress } = props;

  const { match } = useResponsive();
  const { formatMessage } = useIntl();

  // ─── Carousel ──────────────────────────────────────────────────────────────

  const {
    value: isInteractingWithCarousel,
    toggleOn: startCarouselInteraction,
    toggleOff: endCarouselInteraction,
  } = useToggleState(false);

  const carouselRef = useRef<CarouselRef>(undefined);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const canGoToPreviousSlide = activeSlideIndex > 0;
  const canGoToNextSlide = activeSlideIndex < content.length - 1;

  const goToSlide = useCallback((slideIndex: number) => {
    carouselRef.current?.goToSlide(slideIndex);
  }, []);
  const goToPreviousSlide = useCallback(() => {
    carouselRef.current?.goToPrevSlide();
  }, []);
  const goToNextSlide = useCallback(() => {
    carouselRef.current?.goToNextSlide();
  }, []);

  // reset carousel index if content changes.
  useEffect(() => {
    carouselRef.current?.goToSlide(0);
    setActiveSlideIndex(0);
  }, [content]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View>
      <Carousel
        hideDots
        transitionEffect={match(['none', 'fade'])}
        carouselRef={carouselRef}
        style={styles.carousel}
        navControlAccessibilityLabel={formatMessage(messages.homeBannerAccessibilityLabel)}
        onActiveSlideChange={setActiveSlideIndex}
        onBegin={startCarouselInteraction}
        onFinalize={endCarouselInteraction}
      >
        {content.map((item) => (
          <Content
            {...item}
            key={item.id}
            style={style}
            size={size}
            length={content.length}
            isInteractingWithCarousel={isInteractingWithCarousel}
            secondaryCtaLabel={secondaryCtaLabel}
            onSecondaryCtaPress={onSecondaryCtaPress}
          />
        ))}
      </Carousel>

      {content.length > 1 ? (
        <Controls
          content={content}
          activeSlideIndex={activeSlideIndex}
          goToSlide={goToSlide}
          canGoToPreviousSlide={canGoToPreviousSlide}
          canGoToNextSlide={canGoToNextSlide}
          goToPreviousSlide={goToPreviousSlide}
          goToNextSlide={goToNextSlide}
        />
      ) : null}
    </View>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const Content = (props: ContentProps) => {
  const {
    id,
    title,
    description,
    cta,
    image,
    imageA11y,
    deepLink,
    foregroundColor = theme.colors.WHITE,
    size,
    length,
    style,
    isInteractingWithCarousel,
    secondaryCtaLabel,
    onSecondaryCtaPress,
  } = props;

  // ───────────────────────────────────────────────────────────────────────────

  const openUrl = useOpenURL();
  const { track } = useTelemetry();

  const { match, currentBreakpoint } = useResponsive();

  // ─── Styles ────────────────────────────────────────────────────────────────

  const dynamicContainerSize = size === 'small' ? styles.smallerHeight : styles.largerHeight;
  const containerStyle = match([dynamicContainerSize, styles.containerSM]);

  const contentStyleXS = size === 'small' ? styles.contentXSSmall : styles.contentXSLarge;
  const contentStyleWithDotsXS = [contentStyleXS, length > 1 ? styles.paddingDotsXS : null];
  const contentStyle = match([contentStyleWithDotsXS, styles.contentSM]);

  const descriptionFont = match([TYPE_CONFIG.BODY['16'], TYPE_CONFIG.BODY['24']]);
  const dynamicTextColor = useStyle(() => ({ color: foregroundColor }), [foregroundColor]);

  const ctaStyle = match([styles.ctaXS, styles.ctaSM]);

  const contentfulOptions = match([
    size === 'small' ? CONTENTFUL_OPTIONS_XS_SMALL : CONTENTFUL_OPTIONS_XS_LARGE,
    CONTENTFUL_OPTIONS_SM,
    CONTENTFUL_OPTIONS_LG,
    CONTENTFUL_OPTIONS_XL,
  ]);

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const handleCtaPress = useCallback(() => {
    if (!deepLink) return;

    track('home.banner.cta_tapped', { id, destination: deepLink });

    openUrl(deepLink);
  }, [deepLink, id, openUrl, track]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Wrapper
      style={[containerStyle, style]}
      cta={cta}
      deepLink={deepLink}
      isInteractingWithCarousel={isInteractingWithCarousel}
      onPress={handleCtaPress}
    >
      <FallbackImage
        style={styles.image}
        aria-label={imageA11y}
        baseUrl={image}
        defaultImage={HomeBannerFallbackImage}
        contentfulOptions={contentfulOptions}
      />

      <Container
        removeSpacing={currentBreakpoint.isXS}
        style={[
          match([styles.contentInnerWrapperXS, styles.contentInnerWrapperSM]),
          pointerEventsStyles['box-none'],
        ]}
        wrapperStyle={[styles.contentWrapper, pointerEventsStyles['box-none']]}
      >
        <View style={contentStyle}>
          {title ? (
            <DisplayText bold sizeMatch={['24', '56']} style={dynamicTextColor}>
              {title}
            </DisplayText>
          ) : null}

          {description ? (
            <ContentfulTextLinkify style={[descriptionFont, dynamicTextColor]}>
              {description}
            </ContentfulTextLinkify>
          ) : null}

          {deepLink && cta ? (
            <View style={styles.ctaContainer}>
              <Button
                palette="whiteFill"
                aria-label={cta}
                size={match(['medium', 'large'])}
                style={ctaStyle}
                onPress={handleCtaPress}
              >
                {cta}
              </Button>

              {secondaryCtaLabel && onSecondaryCtaPress ? (
                <Button
                  palette="whiteFill"
                  aria-label={secondaryCtaLabel}
                  size={match(['medium', 'large'])}
                  style={ctaStyle}
                  onPress={onSecondaryCtaPress}
                >
                  {secondaryCtaLabel}
                </Button>
              ) : null}
            </View>
          ) : null}
        </View>
      </Container>
    </Wrapper>
  );
};

const Wrapper = (props: WrapperProps) => {
  const { children, style, cta, deepLink, isInteractingWithCarousel, onPress } = props;

  const { formatMessage } = useIntl();

  if (deepLink && !cta) {
    return (
      <Pressable
        accessibilityRole="link"
        aria-label={formatMessage(messages.homeBannerLinkAccessibilityLabel, { link: deepLink })}
        disabled={Platform.OS === 'web' && isInteractingWithCarousel}
        style={style}
        onPress={onPress}
      >
        {children}
      </Pressable>
    );
  }

  return <View style={style}>{children}</View>;
};

const Controls = (props: ControlsProps) => {
  const {
    content,
    activeSlideIndex,
    goToSlide,
    canGoToPreviousSlide,
    canGoToNextSlide,
    goToPreviousSlide,
    goToNextSlide,
  } = props;

  const { match, currentBreakpoint } = useResponsive();
  const { formatMessage } = useIntl();

  return (
    <Container
      removeSpacing={currentBreakpoint.isXS}
      style={[
        match([styles.carouselControlsXS, styles.carouselControlsSM]),
        pointerEventsStyles['box-none'],
      ]}
      wrapperStyle={[styles.carouselControlsWrapper, pointerEventsStyles['box-none']]}
    >
      <View style={styles.carouselControls}>
        <CarouselDots
          layout="standard"
          colors={getCarouselDotsColors(content, activeSlideIndex)}
          style={styles.carouselDots}
          slidesCount={content.length}
          activeSlideIndex={activeSlideIndex}
          accessibilityLabel={formatMessage(messages.carouselDotsAccessibilityLabel)}
          onPress={goToSlide}
        />

        {!currentBreakpoint.isXS ? (
          <>
            <IconLink
              name="IconArrowLeft"
              iconSize={24}
              disabled={!canGoToPreviousSlide}
              accessibilityLabel={formatMessage(messages.previousSlide)}
              style={[styles.carouselArrow, !canGoToPreviousSlide && styles.carouselArrowDisabled]}
              onPress={goToPreviousSlide}
            />

            <IconLink
              name="IconArrowRight"
              iconSize={24}
              disabled={!canGoToNextSlide}
              accessibilityLabel={formatMessage(messages.nextSlide)}
              style={[styles.carouselArrow, !canGoToNextSlide && styles.carouselArrowDisabled]}
              onPress={goToNextSlide}
            />
          </>
        ) : null}
      </View>
    </Container>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const BANNER_MIN_HEIGHT_SM = 800;
const BANNER_MAX_HEIGHT_SM = 1080;

const IMAGE_HEIGHT_XS_SM = 160;
const IMAGE_HEIGHT_XS_LG = 380;
const IMAGE_WIDTH_SM = 768;
const IMAGE_WIDTH_LG = 1440;
const IMAGE_WIDTH_XL = 1920;

const SHARED_CONTENTFUL_OPTIONS: Partial<ContentfulImageTransformOptions> = {
  fm: 'webp',
  fit: 'fill',
  q: 70,
};

const CONTENTFUL_OPTIONS_XS_SMALL: Partial<ContentfulImageTransformOptions> = {
  ...SHARED_CONTENTFUL_OPTIONS,
  h: IMAGE_HEIGHT_XS_SM,
};

const CONTENTFUL_OPTIONS_XS_LARGE: Partial<ContentfulImageTransformOptions> = {
  ...SHARED_CONTENTFUL_OPTIONS,
  h: IMAGE_HEIGHT_XS_LG,
};

const CONTENTFUL_OPTIONS_SM: Partial<ContentfulImageTransformOptions> = {
  ...SHARED_CONTENTFUL_OPTIONS,
  w: IMAGE_WIDTH_SM,
};

const CONTENTFUL_OPTIONS_LG: Partial<ContentfulImageTransformOptions> = {
  ...SHARED_CONTENTFUL_OPTIONS,
  w: IMAGE_WIDTH_LG,
};

const CONTENTFUL_OPTIONS_XL: Partial<ContentfulImageTransformOptions> = {
  ...SHARED_CONTENTFUL_OPTIONS,
  w: IMAGE_WIDTH_XL,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getCarouselDotsColors(content: HomeBannerContent[], activeSlideIndex: number) {
  const color = content[activeSlideIndex]?.foregroundColor ?? theme.colors.WHITE;

  return {
    inactive: {
      idle: color,
      hover: color,
      opacity: 0.3,
    },
    active: {
      idle: color,
      hover: color,
    },
  };
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  carousel: {
    backgroundColor: theme.colors.DARK_KALE,
  },
  carouselControlsWrapper: {
    ...StyleSheet.absoluteFill,
    zIndex: 1,
    justifyContent: 'flex-end',
  },
  carouselControlsXS: {
    paddingHorizontal: theme.spacing['4'],
    paddingVertical: theme.spacing['6'],
  },
  carouselControlsSM: {
    alignItems: 'flex-end',
    paddingVertical: theme.spacing['10'],
  },
  carouselControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['6'],
  },
  containerSM: {
    minHeight: BANNER_MIN_HEIGHT_SM,
    maxHeight: BANNER_MAX_HEIGHT_SM,
  },
  image: {
    ...StyleSheet.absoluteFill,
    backgroundColor: theme.colors.FOREST,
  },
  carouselDots: {
    marginVertical: 0, // overrides default dots vertical margin of 16px
    justifyContent: 'flex-start',
  },
  carouselArrow: {
    width: 40,
    height: 40,
    borderRadius: 40,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.WHITE,
  },
  carouselArrowDisabled: {
    opacity: 0.3,
  },
  smallerHeight: {
    minHeight: IMAGE_HEIGHT_XS_SM,
  },
  largerHeight: {
    minHeight: IMAGE_HEIGHT_XS_LG,
  },
  contentWrapper: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  contentInnerWrapperXS: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing['6'],
    paddingVertical: theme.spacing['6'],
    gap: theme.spacing['4'],
  },
  contentInnerWrapperSM: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    paddingVertical: theme.spacing['10'],
    gap: theme.spacing['4'],
  },
  contentXSSmall: {
    flex: 1,
    gap: theme.spacing['2'],
  },
  contentXSLarge: {
    flex: 1,
    gap: theme.spacing['4'],
  },
  paddingDotsXS: {
    paddingBottom: theme.spacing['8'],
  },
  contentSM: {
    flex: 1,
    alignItems: 'flex-start',
    width: '45%',
    gap: theme.spacing['6'],
  },
  ctaContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['4'],
  },
  ctaXS: {
    alignSelf: 'flex-start',
  },
  ctaSM: {
    alignSelf: 'flex-start',
    minWidth: 150,
    minHeight: 64,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  homeBannerAccessibilityLabel: {
    defaultMessage: 'Home Banner',
    description: 'Home | Home Banner | Accessibility Label',
  },
  homeBannerLinkAccessibilityLabel: {
    defaultMessage: 'Open {link}',
    description: 'Home | Home Banner Link | Accessibility Label',
  },
  carouselDotsAccessibilityLabel: {
    defaultMessage: 'Home Banner Carousel Dots',
    description: 'Home | Home Banner | Carousel Dots | Accessibility Label',
  },
  previousSlide: {
    defaultMessage: 'Previous Slide',
    description: 'Home | Home Banner | Carousel | Previous Slide | Accessibility Label',
  },
  nextSlide: {
    defaultMessage: 'Next Slide',
    description: 'Home | Home Banner | Carousel | Next Slide | Accessibility Label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ContentSize = 'small' | 'large';

type HomeBannerProps = {
  style?: StyleProp<ViewStyle>;
  content: HomeBannerContent[];
  size: ContentSize;
  secondaryCtaLabel?: string;
  onSecondaryCtaPress?: () => void;
};

type ContentProps = HomeBannerContent & {
  style?: StyleProp<ViewStyle>;
  isInteractingWithCarousel: boolean;
  size: ContentSize;
  length: number;
  secondaryCtaLabel?: string;
  onSecondaryCtaPress?: () => void;
};

type WrapperProps = {
  children: PropsWithChildren['children'];
  style?: StyleProp<ViewStyle>;
  cta?: string;
  deepLink?: string;
  isInteractingWithCarousel: boolean;
  onPress: () => void;
};

type ControlsProps = {
  content: HomeBannerContent[];
  activeSlideIndex: number;
  goToSlide: (slideIndex: number) => void;
  canGoToPreviousSlide: boolean;
  canGoToNextSlide: boolean;
  goToPreviousSlide: () => void;
  goToNextSlide: () => void;
};

type CarouselRef = NonNullable<ComponentProps<typeof Carousel>['carouselRef']>['current'];
